import { system } from '@workday/canvas-tokens-web';
import { useIntl } from '@workday/i18n/nextgen';
import { useConstCallback } from '@workday/wcp-cargo-react';
import { useContext, useEffect, useRef } from 'react';
import { PopupContext } from 'modules/common/components/PopupContext';
import ExternalLinkWarningModal from '../components/ExternalLinkWarningDialog/ExternalLinkWarningModal';
import { isExternalLinkWarningEnvironment, shouldShowExternalLinkWarning } from '../utils/externalLinkUtils';

/**
 * Hook that provides external link blocking when `EXTERNAL_LINK_BLOCKING_ENABLED` is true.
 * Uses the Navigation API when available for optimal performance, with click event
 * listeners as a fallback for browsers that don't support the Navigation API.
 * Shows a warning dialog when users try to navigate to external URLs.
 *
 * This hook uses PopupContext to display the warning dialog, eliminating the need
 * for local state management and simplifying the component architecture.
 */
export function useExternalLinkBlocking(): void {
  const popupContext = useContext(PopupContext);
  const intl = useIntl();
  const pendingUrlRef = useRef<string>('');

  const MODAL_HEADING = intl.formatMessage({
    id: 'core.ExternalLinkBlocking.modalHeading',
    defaultMessage: 'Leaving Workday Environment',
    description: 'Heading for the external link warning modal dialog',
  });

  const showWarningDialog = useConstCallback((url: string) => {
    pendingUrlRef.current = url;

    popupContext.display(
      <ExternalLinkWarningModal
        onConfirm={() => {
          popupContext.closeAll();
          if (pendingUrlRef.current) {
            // Open external link in new tab
            window.open(pendingUrlRef.current, '_blank', 'noopener,noreferrer');
          }
          pendingUrlRef.current = '';
        }}
        onCancel={() => {
          popupContext.closeAll();
          pendingUrlRef.current = '';
        }}
      />,
      {
        heading: MODAL_HEADING,
        padding: `var(${system.space.x8})`,
      }
    );
  });

  useEffect(() => {
    if (!isExternalLinkWarningEnvironment()) {
      return () => {};
    }

    // Optimized click handler for new tab/window scenarios
    const handleClick = (event: MouseEvent) => {
      // Early exit for non-link clicks (performance optimization)
      const target = event.target as HTMLElement;
      const anchor = target.closest('a');
      if (!anchor) return;

      // Early exit for links without href
      const href = anchor.getAttribute('href');
      if (!href) return;

      // Check if this will open in a new tab/window
      const willOpenInNewTab = anchor.target === '_blank' || event.ctrlKey || event.metaKey || event.button === 1; // middle click

      // For new tab scenarios, check if external and intercept
      if (willOpenInNewTab) {
        if (shouldShowExternalLinkWarning(href)) {
          event.preventDefault();
          event.stopPropagation();
          showWarningDialog(href);
        }
        return;
      }

      // For same-tab navigation, only intercept if Navigation API is not available
      const useNavigationAPI = 'navigation' in window;
      if (!useNavigationAPI && shouldShowExternalLinkWarning(href)) {
        event.preventDefault();
        event.stopPropagation();
        showWarningDialog(href);
      }
    };

    // Set up click listener with optimized options
    document.addEventListener('click', handleClick, {
      capture: true,
      passive: false,
    });

    // Set up Navigation API for same-tab navigation if available
    const useNavigationAPI = 'navigation' in window;
    let navigationCleanup: (() => void) | undefined;

    if (useNavigationAPI) {
      const handleNavigation = (event: NavigateEvent) => {
        if (!event.userInitiated || !event.canIntercept) return;

        const destinationUrl = event.destination.url;
        if (shouldShowExternalLinkWarning(destinationUrl)) {
          event.intercept({
            handler() {
              showWarningDialog(destinationUrl);
            },
          });
        }
      };

      window.navigation!.addEventListener('navigate', handleNavigation);
      navigationCleanup = () => {
        window.navigation!.removeEventListener('navigate', handleNavigation);
      };
    }

    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
      navigationCleanup?.();
    };
  }, [showWarningDialog]);
}
